/**
 * Shared Chat & Conversation Types
 *
 * This module provides:
 * - Core conversation types for state management (protocol-agnostic, extensible)
 * - UI rendering types for Chat components
 * - Shared enums, constants, and utilities
 *
 * Design goals:
 * - Keep IO/protocol-specific concerns out of the contract
 * - Keep app-specific domain fields out of the contract (apps map at boundaries)
 * - Provide base types that apps can extend
 */
// ============================================================================
// Enums & Constants
// ============================================================================
/**
 * Conversation asking status state machine.
 * - Idle: No active conversation
 * - Init: Initialized with context, ready to start asking
 * - Answering: Waiting for AI response
 * - Error: An error occurred
 */
export const AskStatus = {
    Idle: "idle",
    Init: "init",
    Answering: "answering",
    Error: "error",
};
/**
 * Message roles for conversations.
 */
export const MessageRole = {
    User: "user",
    Assistant: "assistant",
    System: "system",
};
export const ReasoningStepType = {
    ConsultantSearchWeb: "consultant_search_web",
    ConsultantFetchWebContent: "consultant_fetch_web_content",
    ConsultantRetrieveContextObjs: "consultant_retrieve_context_objs",
    ConsultantGetContextObjLines: "consultant_get_context_obj_lines",
    ConsultantListKbFiles: "consultant_list_kb_files",
    ConsultantCallAgent: "consultant_call_agent",
    ConsultantMemory: "consultant_memory",
    ConsultantGenerateImage: "consultant_generate_image",
    ConsultantHttpRequest: "consultant_http_request",
    ConsultantTabularQuery: "consultant_tabular_query",
    ConsultantAnalyzeHistories: "consultant_analyze_histories",
    /**
     * Generic lookup step (agents, histories, attachments, evaluators, etc.).
     * Backend emits: "consultant_lookup".
     */
    ConsultantLookup: "consultant_lookup",
    /**
     * Backward-compat alias (older clients may emit "consultant_lookup_agent").
     * Prefer `ConsultantLookup`.
     */
    ConsultantLookupAgent: "consultant_lookup_agent",
    /**
     * Human-in-the-loop form request step.
     * Backend emits: "consultant_request_form".
     */
    ConsultantRequestForm: "consultant_request_form",
    ConsultantFinished: "consultant_finished",
};
/**
 * Result type for reasoning step results.
 */
export const ReasoningStepResultType = {
    Text: "text",
    List: "list",
    Json: "json",
    Links: "links",
    Tabular: "tabular",
};
// ============================================================================
// Placeholder Constants
// ============================================================================
/**
 * Placeholder ID for user message during streaming.
 * Using negative IDs to distinguish from real server-assigned IDs.
 */
export const PLACEHOLDER_USER_ID = -1;
/**
 * Placeholder ID for assistant message during streaming.
 */
export const PLACEHOLDER_ASSISTANT_ID = -2;
/**
 * Status of a reasoning step execution.
 */
export const ReasoningStepStatus = {
    Running: "running",
    Completed: "completed",
    Failed: "failed",
};
// ============================================================================
// Utilities
// ============================================================================
/**
 * Check if a conversation is a placeholder (not yet persisted).
 */
export function isPlaceholder(conversation) {
    return (conversation.id === undefined ||
        conversation.id === PLACEHOLDER_USER_ID ||
        conversation.id === PLACEHOLDER_ASSISTANT_ID);
}
